import React, { useContext, useEffect, useSyncExternalStore } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { isPhoneFormFactor } from 'utils/formFactor';

// Native OS orientation lock.
//
// PHONES (iPhone, Android below sw600dp) are portrait-only, app-wide
// (EN-10663): the lock is held for the life of the app, whichever view is
// active. The native layers hold the same rule on their own —
// AppDelegate's supportedInterfaceOrientationsFor(_:) on iOS, MainActivity's
// setRequestedOrientation on Android — so this is the web bundle's half of a
// belt-and-braces pair, and the half that reaches installs which have not yet
// picked up the native build.
//
// TABLETS keep the original behaviour. Info.plist / AndroidManifest let them
// rotate freely (the table view IS designed for landscape). Without any lock,
// rotating to landscape while the lobby is showing plays the OS's rotation
// animation and briefly shows the portrait-designed lobby laid out sideways
// before ForcePortrait's CSS trick snaps it back — a jarring "flash to
// landscape then back" every rotation. So while the lobby is the active view
// we ask the OS to hold portrait; while a table or the grid is active we
// release the lock so the table can rotate normally. The plugin's `unlock`
// restores the manifest's declared support.
//
// THE LOCK IS RE-ASSERTED, NOT JUST TAKEN (review #3838). On iOS 16+ the
// plugin's `lock` resolves as soon as it has *asked* for the geometry update —
// `requestGeometryUpdate`'s completion is not awaited — so a refused request
// looks like success here and nothing would retry it. Two hooks close that
// gap: the policy is re-applied whenever the app returns to the foreground
// (`appStateChange` → active), and whenever the plugin reports an orientation
// this component did not ask for (`screenOrientationChange` to a landscape
// while portrait is held). Both are idempotent on a phone.
//
// FORM FACTOR IS LIVE. A foldable is a phone folded (~373 dp) and a tablet
// unfolded (~673 dp) without ever relaunching; `window.screen` changes and the
// WebView fires `resize`, so the phone/tablet verdict is re-read on resize and
// the policy follows it. (MainActivity does the same natively in
// onConfigurationChanged.)
//
// Non-native platforms (mobile web / desktop) fall back to the CSS rotation in
// ForcePortrait — the plugin's web implementation is a no-op here.

const subscribeToScreenChanges = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
  };
};

/** Phone or tablet, re-read whenever the screen changes size (a fold). */
const usePhoneFormFactor = (): boolean =>
  useSyncExternalStore(subscribeToScreenChanges, isPhoneFormFactor, isPhoneFormFactor);

const NativeOrientationLock: React.FC = () => {
  const { activeView } = useContext(ViewManagerContext);
  const phone = usePhoneFormFactor();
  const holdPortrait = phone || activeView === 'lobby';

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const apply = async () => {
      try {
        if (holdPortrait) {
          await ScreenOrientation.lock({ orientation: 'portrait' });
        } else {
          await ScreenOrientation.unlock();
        }
      } catch {
        // Lock/unlock is best-effort — a failure just falls back to the
        // manifest's declared orientations (portrait-only on phones since
        // EN-10663) plus ForcePortrait's CSS rotation.
      }
    };

    apply();

    // Re-assert on every return to the foreground: a resume into landscape on
    // a phone whose geometry request was refused stays landscape otherwise.
    const resume = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) apply();
    });
    // Re-assert when the OS reports an orientation we are holding against.
    const drift = ScreenOrientation.addListener('screenOrientationChange', ({ type }) => {
      if (holdPortrait && !type.startsWith('portrait')) apply();
    });
    const detach = (handle: typeof resume) => {
      Promise.resolve(handle)
        .then((h) => h.remove())
        .catch(() => {
          /* noop — listener never attached */
        });
    };

    return () => {
      detach(resume);
      detach(drift);
      // A phone never gives the lock back — not on a view change, not on app
      // teardown. Tablets release it on unmount so nothing lingers if the
      // component tree ever remounts under a different lifecycle.
      if (phone || !Capacitor.isNativePlatform()) return;
      ScreenOrientation.unlock().catch(() => {
        /* noop — see apply() */
      });
    };
  }, [holdPortrait, phone]);

  return null;
};

export default NativeOrientationLock;
